import { PageComponent } from "./components/page_component";
import { apiClient } from "./client";
import translate, { translatedPropertyFromKey } from "./locales";
import { NextStatements } from "./components/next_statements";
import { CollapsibleRadio } from "./components/collapsible_radio";
import { STATEMENT_KEYS, STATEMENTS } from "./data/statements";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}
const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

export function surveySettingsForStatement(statement) {
  const answeredStatements = apiClient.treesponses.map((s) =>
    s.getAnswer("statement"),
  );
  const availableStatements = STATEMENT_KEYS.filter(
    (s) => !answeredStatements.includes(s) && statement !== s,
  ).sort((s1, s2) => STATEMENTS[s1].order - STATEMENTS[s2].order);

  const statementName = translatedPropertyFromKey([
    "statements",
    statement,
    "name",
  ]);
  return {
    key: `bankforum-${statement}`,
    pages: [
      {
        key: `comment-${statement}`,
        order: 1,
        component: PageComponent,
        statement,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 500,
            title: statementName,
            description: translatedPropertyFromKey("comment.description"),
            placeholder: translatedPropertyFromKey("comment.placeholder"),
            aiResponseTexts,
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_name",
            dependsOn: ["opinion"],
            computeValue: (treesponse) =>
              translate(
                ["statements", statement, "bpartName"],
                {},
                treesponse.language,
              ),
          },
        ],
      },
      {
        key: "name",
        order: 4,
        component: PageComponent,
        title: translatedPropertyFromKey(["name", "intro", "title"]),
        skipIf: (treesponse) => {
          const comment = treesponse.getAnswer("comment");
          return !(typeof comment === "string" && comment.trim().length > 0);
        },
        description: translatedPropertyFromKey([
          "name",
          "intro",
          "description",
        ]),
        statement,
        sections: [
          {
            order: 1,
            type: "string",
            key: "firstName",
            title: translatedPropertyFromKey(["name", "firstName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("firstName"),
          },
          {
            order: 2,
            type: "string",
            key: "lastName",
            title: translatedPropertyFromKey(["name", "lastName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("lastName"),
          },
          {
            order: 3,
            type: "checkbox",
            key: "anonymous",
            title: translatedPropertyFromKey(["name", "anonymous", "title"]),
            defaultValue: () => getPreviousAnswer("anonymous"),
          },
          {
            order: 4,
            type: "hidden",
            key: "completed",
            defaultValue: true,
          },
          {
            order: 5,
            type: "hidden",
            key: "finalName",
            computeValue: (treesponse) => {
              if (treesponse.getAnswer("anonymous")) {
                return translate(
                  "name.anonymous.value",
                  {},
                  treesponse.language,
                );
              } else {
                return treesponse.getAnswer("firstName");
              }
            },
            dependsOn: ["firstName", "anonymous"],
          },
        ],
      },
      {
        key: "sociodemo",
        order: 5,
        component: PageComponent,
        title: translatedPropertyFromKey(["sociodemo", "intro", "title"]),
        description: translatedPropertyFromKey([
          "sociodemo",
          "intro",
          "description",
        ]),
        // We skip this page if the user answered the same name as their previous answer
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) =>
              !!treesponse.getAnswer(key) &&
              treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            order: 1,
            type: "radio",
            key: "employed",
            title: translatedPropertyFromKey([
              "sociodemo",
              "employed",
              "title",
            ]),
            options: ["yes", "no"].map((key) => ({
              label: translatedPropertyFromKey([
                "sociodemo",
                "employed",
                "options",
                key,
              ]),
              value: key,
            })),
            defaultValue: () => getPreviousAnswer("employed"),
            required: true,
            component: CollapsibleRadio,
          },
          {
            order: 2,
            type: "number",
            key: "yearOfBirth",
            title: translatedPropertyFromKey([
              "sociodemo",
              "yearOfBirth",
              "title",
            ]),
            options: [],
            defaultValue: () => getPreviousAnswer("yearOfBirth"),
            maxLength: 4,
            required: true,
            placeholder: translatedPropertyFromKey([
              "sociodemo",
              "yearOfBirth",
              "placeholder",
            ]),
          },
          {
            order: 3,
            type: "radio",
            key: "gender",
            title: translatedPropertyFromKey(["sociodemo", "gender", "title"]),
            options: ["m", "v", "x"].map((key) => ({
              label: translatedPropertyFromKey([
                "sociodemo",
                "gender",
                "options",
                key,
              ]),
              value: key,
            })),
            defaultValue: () => getPreviousAnswer("gender"),
            required: true,
            component: CollapsibleRadio,
          },
          {
            order: 4,
            type: "string",
            key: "postcode",
            title: translatedPropertyFromKey([
              "sociodemo",
              "postcode",
              "title",
            ]),
            options: [],
            defaultValue: () => getPreviousAnswer("postcode"),
            maxLength: 6,
            required: true,
            placeholder: translatedPropertyFromKey([
              "sociodemo",
              "postcode",
              "placeholder",
            ]),
          },
        ],
      },
      {
        key: "like",
        order: 6,
        component: PageComponent,
        statement,
        sections: [
          {
            type: "like",
            order: 1,
            key: "like",
            title: translatedPropertyFromKey(["like", "title"]),
            description: translatedPropertyFromKey(["like", "description"]),
            aggregatorSettings: {
              url: `${aggregatorURL}/bankforum`,
              projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
            },
          },
        ],
      },
      {
        key: "optin",
        order: 7,
        component: PageComponent,
        title: translatedPropertyFromKey(["optin", "intro", "title"]),
        // We skip this page if the user answered the same name as their previous answer
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) =>
              !!treesponse.getAnswer(key) &&
              treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            type: "checkbox",
            order: 1,
            title: translatedPropertyFromKey([
              "optin",
              "optinProject",
              "title",
            ]),
            key: "optinProject",
            htmlAttributes: { class: "question--switch" },
          },
          {
            type: "checkbox",
            order: 2,
            title: translatedPropertyFromKey([
              "optin",
              "optinOrganisation",
              "title",
            ]),
            key: "optinOrganisation",
            htmlAttributes: { class: "question--switch" },
          },
          {
            type: "email",
            order: 3,
            skipIf: (treesponse) =>
              treesponse.getAnswer("optinProject") !== true &&
              treesponse.getAnswer("optinOrganisation") !== true,
            dependsOn: ["optinProject", "optinOrganisation"],
            title: translatedPropertyFromKey(["optin", "email", "title"]),
            key: "email",
            customValidations: [
              (treesponse, _key, value) => {
                if (
                  (treesponse.getAnswer("optinProject") ||
                    treesponse.getAnswer("optinOrganisation")) &&
                  (value === null || value === undefined || value.trim() === "")
                ) {
                  return [
                    false,
                    translatedPropertyFromKey(["optin", "email", "required"]),
                  ];
                }

                return [true, null];
              },
            ],
          },
        ],
      },
      {
        key: "next",
        order: 8,
        component: PageComponent,
        title: translatedPropertyFromKey(["next", "intro", "title"]),
        description: translatedPropertyFromKey([
          "next",
          "intro",
          availableStatements.length > 0
            ? "description"
            : "description_all_done",
        ]),
        sections: [
          {
            type: "content",
            component: NextStatements,
            order: 1,
            availableStatements,
          },
        ],
        showNext: false,
        htmlAttributes: {
          class: "survey--compact",
        },
      },
    ],
  };
}
